import * as amplitude from '@amplitude/analytics-browser';
import { Identify } from '@amplitude/analytics-browser';
import { useEffect, useRef } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { organizationIdFromClaims } from '@/lib/studioIdentity';

import { StudioEvents, trackStudio } from './events';

function apiKey(): string {
  return (import.meta.env.VITE_AMPLITUDE_API_KEY || '').trim();
}

let initPromise: Promise<boolean> | null = null;

/** Idempotent Browser SDK init. Returns false when key missing / init fails. */
export function initAmplitudeBrowser(): Promise<boolean> {
  const key = apiKey();
  if (!key) return Promise.resolve(false);
  if (!initPromise) {
    initPromise = (async () => {
      try {
        await amplitude.init(key, undefined, {
          defaultTracking: {
            sessions: true,
            pageViews: false,
            formInteractions: false,
            fileDownloads: false,
          },
        }).promise;
        return true;
      } catch (error) {
        console.warn('[amplitude] init skipped:', error);
        initPromise = null;
        return false;
      }
    })();
  }
  return initPromise;
}

/**
 * Standard Amplitude Browser SDK bootstrap for rsos/Studio.
 * Identify Auth0 `sub` only; fire `studio.signed_in` on login; reset on logout.
 * Safe no-op without `VITE_AMPLITUDE_API_KEY`.
 */
export function AmplitudeBootstrap() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const signedInFor = useRef<string | null>(null);

  useEffect(() => {
    if (!apiKey()) return;
    void initAmplitudeBrowser();
  }, []);

  useEffect(() => {
    if (isLoading || !apiKey()) return;
    if (!isAuthenticated) {
      signedInFor.current = null;
      try {
        amplitude.reset();
      } catch {
        /* ignore */
      }
      return;
    }
    const userId = typeof user?.sub === 'string' ? user.sub : undefined;
    if (!userId) return;
    const orgId =
      organizationIdFromClaims(user as Record<string, unknown>) ||
      (import.meta.env.VITE_AUTH0_ORGANIZATION || '').trim() ||
      undefined;

    void (async () => {
      const ok = await initAmplitudeBrowser();
      if (!ok) return;
      amplitude.setUserId(userId);
      if (orgId) {
        const identify = new Identify();
        identify.set('orgId', orgId);
        amplitude.identify(identify);
      }
      if (signedInFor.current !== userId) {
        signedInFor.current = userId;
        trackStudio(StudioEvents.StudioSignedIn, { orgId });
      }
    })();
  }, [isAuthenticated, isLoading, user]);

  return null;
}
